import { axiosInstance } from "@/config/axios-wrapper";
import { V2ListResult, V2Response } from "@/types/api";

/*
 * Every function here unwraps the V2 envelope and returns `response.data.result`.
 * Callers must consume the result shape directly — reading `.result` (or `.data`
 * on an already-bare array) a second time is the double-unwrap bug that kept
 * the purchase-history and request-history lists permanently empty.
 *
 * The interfaces below describe each endpoint's `result` as observed in dev
 * responses and consumer usage. Fields the backend may omit are optional;
 * raw status strings stay `string` because src/utils/bcStatus.ts owns mapping
 * them (unknown values must degrade there, not in the type system).
 */

/** Kredifi background-check package (background-check/packages). */
export interface BcPackage {
  pkg_id: string;
  name: string;
  description: string;
  price: number;
  is_ny: boolean;
  document_ids: string[];
  created_at: string;
  updated_at: string;
  __v: number;
  for_js: boolean;
  order: number;
  kred_pkg_id: string;
  id: string;
  features?: string[];
}

/** Package summary as embedded in applications, purchases and requests. */
export interface BcPackageRef {
  id?: string;
  pkg_id?: string;
  name: string;
  price?: number;
}

export interface BcJobseekerRef {
  id?: string;
  full_name?: string;
  email?: string;
  photo_thumbnail_url?: string;
}

export interface BcOrganizationRef {
  id?: string;
  name?: string;
  logo_thumbnail_url?: string;
}

export interface BcJobRef {
  title?: string;
  alt_id?: string;
  address?: { city?: string };
}

/** Kredifi report attached to a completed application. */
export interface BcReport {
  pdf_url?: string;
  created_at?: string;
}

export interface BcApplicationAddress {
  country_code?: string;
  country_name?: string;
  state_code?: string;
  state_name?: string;
  city?: string;
  address1?: string;
  postal_code?: string;
  /** ISO date string — the form's Date is serialized over the wire. */
  from_date?: string;
}

export interface BcApplicationLicenseInfo {
  license_number?: string;
  country_code?: string;
  country_name?: string;
  state_code?: string;
  state_name?: string;
}

/**
 * bc_application document: the check itself, from form submission to report.
 * List endpoints embed a slim version (identity + package + dates); the
 * detail endpoint adds the form echo (address, license_info, ssn, dob).
 */
export interface BcApplication {
  id: string;
  /** Raw backend state ("review_pending", "failed", ...) — map via bcStatus.ts. */
  status?: string | null;
  completed_at?: string | null;
  submitted_at?: string | null;
  created_at?: string;
  kredifi_order_id?: string | null;
  /** True when the check reached the Job Seeker through an email invite. */
  invited?: boolean | null;
  bc_request_id?: string | null;
  bc_request?: unknown;
  package?: BcPackageRef;
  job?: BcJobRef | null;
  jobseeker?: BcJobseekerRef;
  /** Populated on employer-requested applications; absent for self-paid. */
  organization?: { id?: string; name?: string; deleted?: boolean } | null;
  report?: BcReport | null;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  /** ISO date string — the form's Date is serialized over the wire. */
  dob?: string;
  social_security_no?: string;
  ny_county?: boolean | null;
  address?: BcApplicationAddress;
  license_info?: BcApplicationLicenseInfo;
}

/** One row of /:userType/:id/bc-purchase-history. */
export interface BcPurchase {
  id: string;
  /** Payment state: "completed" | "pending" | "failed". */
  status?: string;
  created_at?: string;
  package?: BcPackageRef;
}

/**
 * bc-report-request: an employer asking a Job Seeker to share an existing
 * report. status: "sent" | "shared" | "declined" (see getReportRequestStatus).
 */
export interface BcReportRequest {
  id: string;
  status?: string;
  created_at?: string;
  bc_application?: BcApplication | null;
  jobseeker?: BcJobseekerRef;
}

/**
 * bc-request: a check the employer ordered (via chat or email invite). Once
 * the Job Seeker submits the form, `bc_application` is attached and becomes
 * the source of truth for status.
 */
export interface BcRequest {
  id: string;
  /** "invited" | "sent" | "fulfilled" (see getOrderedCheckStatus). */
  status?: string;
  created_at?: string;
  invited?: boolean;
  bc_application?: BcApplication | null;
  jobseeker?: BcJobseekerRef;
  package?: BcPackageRef;
  job?: BcJobRef | null;
  organization?: BcOrganizationRef;
}

/** GET /organizations/:id/bc-requests wraps its list under `bc_requests`, not `data`. */
export interface BcRequestList {
  bc_requests: BcRequest[];
}

/**
 * bc-shared-application: an organization's view of a report it can access.
 * `status` is the access origin ("bought" | "shared"), not the check state —
 * that lives on the embedded `bc_application`.
 */
export interface BcSharedApplication {
  id: string;
  status?: string;
  shared_at?: string;
  bc_application?: BcApplication;
  jobseeker?: BcJobseekerRef;
}

/** A chat the Job Seeker's report can be shared into (result is a bare array). */
export interface BcShareableChat {
  id: string;
  organization: BcOrganizationRef;
  jobseeker?: BcJobseekerRef;
  job?: BcJobRef;
}

/** One share of a report, as listed by /jobseekers/:id/bc-shared-histories. */
export interface BcSharedHistoryEntry {
  id: string;
  organization: BcOrganizationRef;
  job?: BcJobRef | null;
}

/** Employer-side request quota for a shareable application. */
export interface BcShareDetails {
  attempts_left?: number;
  last_requested_at?: string | null;
  last_shared_at?: string | null;
}

/** A completed application an employer can request / a Job Seeker can share. */
export interface BcShareableApplication {
  id: string;
  completed_at?: string | null;
  submitted_at?: string | null;
  is_shared?: boolean;
  jobseeker?: BcJobseekerRef;
  package?: BcPackageRef;
  share_details?: BcShareDetails;
}

/** Contract: SUPPORT_TICKETS_API.md (201 result). */
export interface BcSupportTicket {
  ticket_id: string;
  status: string;
}

/** Stripe checkout session for a bc_application — callers redirect to `url`. */
export interface BcCheckoutSession {
  url: string;
}

/**
 * One invited_email record, as created by POST /background-check/invite
 * (backend inviteJobseekersForBcByEmails). The result lists only the records
 * created for first-time emails; emails invited before (under the 5-send cap)
 * are re-sent but not echoed back, so a successful call can return []. A 2xx
 * always means the invites went out — failures (unknown package/organization,
 * email already registered as a Job Seeker, invalid payload) arrive as thrown
 * 400/404s with the message at error.response.data.error.message, never as a
 * 2xx body.
 */
export interface BcInvite {
  id: string;
  email: string;
  /** Invite category — background-check invites are "bc". */
  type: string;
  invited_by: string;
  organization_id?: string | null;
  pkg_id?: string | null;
  invited_count: number;
  resend_at?: string | null;
  completed_at?: string | null;
  created_at?: string;
  updated_at?: string;
  __v?: number;
}

export const getAllPackages = async (userType: "employer" | "jobseeker"): Promise<BcPackage[]> => {
  const response = await axiosInstance.get<V2Response<BcPackage[]>>(
    `/background-check/packages?user=${userType}`,
  );
  return response.data.result;
};

export const getPackgageById = async (id: string): Promise<BcPackage> => {
  const response = await axiosInstance.get<V2Response<BcPackage>>(
    `/background-check/packages/${id}`,
  );
  return response.data.result;
};

export const getPurchaseHistory = async (
  userType: string,
  userId: string,
): Promise<V2ListResult<BcPurchase>> => {
  const response = await axiosInstance.get<V2Response<V2ListResult<BcPurchase>>>(
    `/${userType}/${userId}/bc-purchase-history`,
  );
  return response.data.result;
};

export interface ApplicationData {
  package_id: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  social_security_no: number;
  dob: Date;
  email: string;
  address: {
    country_code: string;
    state_code: string;
    city: string;
    address1: string;
    postal_code: string;
  };
  phone: string;
}

export const submitApplication = async (data: ApplicationData): Promise<BcApplication> => {
  const response = await axiosInstance.post<V2Response<BcApplication>>(
    "/background-check/bc-applications",
    data,
  );
  return response.data.result;
};

/**
 * One-click support ticket for a failed background check.
 * Contract: SUPPORT_TICKETS_API.md — until the backend ships this endpoint,
 * callers must catch the error and fall back to the prefilled Contact Us form.
 */
export const createBcSupportTicket = async (
  bcApplicationId: string,
  message: string,
): Promise<BcSupportTicket> => {
  const response = await axiosInstance.post<V2Response<BcSupportTicket>>(
    `/background-check/bc-applications/${bcApplicationId}/support-tickets`,
    { message },
  );
  return response.data.result;
};

export const createCheckoutSession = async (payload: {
  pkg_id?: string;
  bc_application_id: string;
  success_url: string;
}): Promise<BcCheckoutSession> => {
  try {
    const response = await axiosInstance.post<V2Response<BcCheckoutSession>>(
      "/background-check/bc-applications/create-checkout-session",
      payload,
    );
    return response.data.result;
  } catch (error: any) {
    console.error("Error creating checkout session:", error);
    throw new Error(error.response?.data?.message || "Failed to create checkout session");
  }
};

export const getAllJobseekerBcApplications = async (
  userId: string,
): Promise<V2ListResult<BcApplication>> => {
  const response = await axiosInstance.get<V2Response<V2ListResult<BcApplication>>>(
    `/jobseekers/${userId}/bc-applications`,
  );
  return response.data.result;
};

export const getApplicationById = async (applicationId: string): Promise<BcApplication> => {
  const response = await axiosInstance.get<V2Response<BcApplication>>(
    `/background-check/bc-applications/${applicationId}`,
  );
  return response.data.result;
};

export const shareReport = async (data: {
  bc_application_id: string;
  chat_ids: string[];
}): Promise<unknown> => {
  const response = await axiosInstance.post<V2Response<unknown>>(
    "/background-check/bc-shared-applications",
    data,
  );
  return response.data.result;
};

export const getShareableChats = async ({
  queryKey,
}: {
  queryKey: string[];
}): Promise<BcShareableChat[]> => {
  const [, searchQuery] = queryKey; // Extract the searchQuery from queryKey
  const response = await axiosInstance.get<V2Response<BcShareableChat[]>>(
    `/background-check/shareable-chats?skip=0&limit=200&q=${searchQuery || ""}`,
  );
  return response.data.result;
};

export const getShareApplicationHistory = async (
  jobseekerId: string,
  applicationId: string,
): Promise<V2ListResult<BcSharedHistoryEntry>> => {
  const response = await axiosInstance.get<V2Response<V2ListResult<BcSharedHistoryEntry>>>(
    `jobseekers/${jobseekerId}/bc-shared-histories?bc_application_id=${applicationId}`,
  );
  return response.data.result;
};

export const getShareableApplications = async (
  jobseekerId: string,
): Promise<V2ListResult<BcShareableApplication>> => {
  const response = await axiosInstance.get<V2Response<V2ListResult<BcShareableApplication>>>(
    `/jobseekers/${jobseekerId}/shareable-bc-applications`,
  );
  return response.data.result;
};

export const requestBcReport = async (data: { bc_application_ids: string[] }): Promise<unknown> => {
  const response = await axiosInstance.post<V2Response<unknown>>(
    "/background-check/bc-report-requests",
    data,
  );
  return response.data.result;
};

export const acceptReportRequestById = async (reportRequestId: string): Promise<unknown> => {
  const response = await axiosInstance.post<V2Response<unknown>>(
    `/background-check/bc-report-requests/${reportRequestId}/accept`,
  );
  return response.data.result;
};

export const declineReportRequestById = async (reportRequestId: string): Promise<unknown> => {
  const response = await axiosInstance.post<V2Response<unknown>>(
    `/background-check/bc-report-requests/${reportRequestId}/decline`,
  );
  return response.data.result;
};

export const getAllOrginzationBcApplications = async (
  orgId: string,
): Promise<V2ListResult<BcSharedApplication>> => {
  const response = await axiosInstance.get<V2Response<V2ListResult<BcSharedApplication>>>(
    `/organizations/${orgId}/bc-shared-applications`,
  );
  return response.data.result;
};

export const getAllOrginzationBcReportRequests = async (
  orgId: string,
): Promise<V2ListResult<BcReportRequest>> => {
  const response = await axiosInstance.get<V2Response<V2ListResult<BcReportRequest>>>(
    `/organizations/${orgId}/bc-report-requests`,
  );
  return response.data.result;
};

export const getAllOrginzationBcRequests = async (orgId: string): Promise<BcRequestList> => {
  const response = await axiosInstance.get<V2Response<BcRequestList>>(
    `/organizations/${orgId}/bc-requests`,
  );
  return response.data.result;
};

export const createBCRequest = async (payload: {
  chat_ids: string[];
  pkg_id: string;
}): Promise<unknown> => {
  const response = await axiosInstance.post<V2Response<unknown>>(
    "/background-check/bc-requests-2",
    payload,
  );
  return response.data.result;
};

export const getJobseekerBcCompliance = async (userId: string): Promise<Blob> => {
  const response = await axiosInstance.get<Blob>(`/jobseekers/${userId}/bc-document`, {
    responseType: "blob",
  });
  return response.data;
};

export const getOrgBcCompliance = async (orgId: string): Promise<Blob> => {
  const response = await axiosInstance.get<Blob>(`/organizations/${orgId}/bc-document`, {
    responseType: "blob",
  });
  return response.data;
};

export const downloadBcComplianceDoc = async (id: string, userName: string, user_type: string) => {
  try {
    let fileData;
    if (user_type === "job_seeker") {
      fileData = await getJobseekerBcCompliance(id);
    } else {
      fileData = await getOrgBcCompliance(id);
    }

    const fileURL = window.URL.createObjectURL(new Blob([fileData]));

    const a = document.createElement("a");
    a.href = fileURL;
    a.download = `compliance-document.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

export const getBcRequestById = async (id: string): Promise<BcRequest> => {
  const response = await axiosInstance.get<V2Response<BcRequest>>(
    `/background-check/bc-requests/${id}`,
  );
  return response.data.result;
};

export const sendInviteForBcRequest = async (data: {
  emails: string[];
  pkg_id: string;
}): Promise<BcInvite[]> => {
  const response = await axiosInstance.post<V2Response<BcInvite[]>>(
    "/background-check/invite",
    data,
  );
  return response.data.result;
};

export const createBCRequestByJobseekerIds = async (data: {
  jobseeker_ids: string[];
  pkg_id: string;
}): Promise<unknown> => {
  const response = await axiosInstance.post<V2Response<unknown>>(
    "/background-check/bc-requests-by-jobseeker-ids",
    data,
  );
  return response.data.result;
};
